import React, { ReactNode, useMemo } from 'react';

import { CustomAlert } from 'components/AlertsCollection';
import ActionsCell, { StudyActionKind } from 'components/Flux/Personalized/MyStudies/ActionsCell';
import StudyRow from 'components/Flux/Personalized/MyStudies/StudyRow';
import { StudyViewer } from 'helper/flux/myStudyRow';
import { MyStudy } from 'types/Flux/Personalized/myStudies';

export interface StudyListProps {
  /** Estudos da página, na ordem do backend. */
  studies: MyStudy[];
  /** Quem vê a tela (memoizado pelo pai): "(você)" e a habilitação das ações. */
  viewer: StudyViewer;
  /** "Agora" das linhas, um valor só para a lista inteira (`useMyStudiesPage().nowMs`). */
  nowMs: number;
  /** Resultado de cada ação, para o aviso único da tela. */
  onFeedback: (feedback: CustomAlert) => void;
  /** Ação concluída, para o destaque da linha (#2004). */
  onActionSuccess?: (studyId: number, kind: StudyActionKind) => void;
  /**
   * Conteúdo da linha aberta (o `ScenariosPanel` da #2002): recebe o estudo e só devolve o
   * componente, sem efeito. ESTÁVEL (`useCallback` na casca): entra nas dependências da
   * memoização das linhas. Sem ele, o chevron fica `aria-disabled`.
   */
  renderExpanded?: (study: MyStudy) => ReactNode;
  /**
   * Linhas destacadas (#2004): as que o polling mudou e as de reenviar ou cancelar com sucesso
   * (D12), o `changedIds` do `useMyStudiesStatusPolling`. ESTÁVEL: o mesmo `Set` enquanto nada
   * muda, porque entra nas dependências da memoização das linhas (um `Set` novo re-renderiza
   * todas). Sem ele, nenhuma linha fica destacada.
   */
  highlightedIds?: ReadonlySet<number>;
}

/**
 * Linhas da página da aba Meus Estudos (#1999): uma `StudyRow` (#1998) por estudo, com a
 * `ActionsCell` (#1994) no slot de ações e o `renderExpanded` (#2002) ligado ao estudo de cada
 * linha.
 *
 * Segue o contrato da `StudyRow`: `key={study.id}` e linhas irmãs diretas, sem invólucro (os 8 px
 * entre elas vêm do CSS da linha). Quem a monta não troca as linhas pelo skeleton em refetch: o
 * `PendingOverlay` as esmaece sem desmontar.
 *
 * As linhas saem de um `useMemo`: o rascunho da busca mora na casca e cada tecla a re-renderiza
 * (e a esta lista). Com as mesmas props, os nós das linhas são os mesmos objetos e o React não
 * re-renderiza nenhuma. Por isso as props precisam ser estáveis: `viewer` e callbacks
 * memoizados, `nowMs` que só muda com a lista ou com o dia, `renderExpanded` e `highlightedIds`
 * estáveis. O relógio do indicador (#2004) fica fora daqui: o tique de 1 s não chega às linhas.
 */
export default function StudyList({
  studies, viewer, nowMs, onFeedback, onActionSuccess,
  renderExpanded, highlightedIds,
}: StudyListProps) {
  const rows = useMemo(() => studies.map((study) => (
    <StudyRow
      key={study.id}
      study={study}
      userId={viewer.userId}
      nowMs={nowMs}
      actions={(
        <ActionsCell
          study={study}
          viewer={viewer}
          onFeedback={onFeedback}
          onActionSuccess={onActionSuccess}
        />
      )}
      renderExpanded={renderExpanded && (() => renderExpanded(study))}
      highlighted={highlightedIds !== undefined && highlightedIds.has(study.id)}
    />
  )), [studies, viewer, nowMs, onFeedback, onActionSuccess, renderExpanded, highlightedIds]);

  // O fragmento é necessário: com o TypeScript 4.9, um componente não pode devolver um array
  // eslint-disable-next-line react/jsx-no-useless-fragment
  return <>{rows}</>;
}
